import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  alpha,
  Paper
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authService } from '../services/authService';
import { pushService } from '../services/pushService';
import usersService from '../services/usersService';
import UserAvatar from '../components/UserAvatar';
import { PhotoCamera, Fingerprint, NotificationsActive, SettingsSuggest, Security, Badge } from '@mui/icons-material';

// Design System: Nautilus Console
const consoleColors = {
  deepOcean: '#0f2134',
  abyssal: '#0a1622',
  sand: '#ccba9b',
  sandDim: 'rgba(204, 186, 155, 0.5)',
  text: '#ffffff',
  textDim: 'rgba(255, 255, 255, 0.7)',
};

const Panel = ({ title, icon, children, mode }: { title: string, icon: React.ReactNode, children: React.ReactNode, mode: string }) => (
  <Card sx={{
    height: '100%',
    backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#ffffff',
    border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
    borderRadius: 0,
    boxShadow: mode === 'dark' ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
  }}>
    <Box sx={{
      p: 1.5,
      borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#eeeeee'}`,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f8f9fa'
    }}>
      <Box sx={{ color: mode === 'dark' ? consoleColors.sand : '#1a1a1a' }}>
        {icon}
      </Box>
      <Typography variant="overline" sx={{
        fontWeight: 600,
        letterSpacing: '0.1em',
        color: mode === 'dark' ? consoleColors.sand : '#1a1a1a'
      }}>
        {title}
      </Typography>
    </Box>
    <CardContent sx={{ p: 3 }}>
      {children}
    </CardContent>
  </Card>
);

const UserProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [subscriptionCount, setSubscriptionCount] = useState(0);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      setLoading(false);
      return;
    }

    try {
      const response = await authService.changePassword(formData.oldPassword, formData.newPassword);
      if (response.data) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to change password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit(event as any);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && user) {
      const file = e.target.files[0];
      try {
        await usersService.uploadProfilePicture(user.id, file);
        window.location.reload();
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to upload profile picture' });
      }
    }
  };

  // Load push notification status
  useEffect(() => {
    const loadPushStatus = async () => {
      if (pushService.isSupported()) {
        const status = await pushService.getSubscriptionStatus();
        setPushEnabled(status.isSubscribed);
        setSubscriptionCount(status.subscriptionCount);
      }
    };
    loadPushStatus();
  }, []);

  const handlePushToggle = async () => {
    setPushLoading(true);
    setMessage(null);
    try {
      if (pushEnabled) {
        await pushService.unsubscribeFromNotifications();
        setPushEnabled(false);
        setSubscriptionCount(0);
        setMessage({ type: 'success', text: 'Push notifications disabled successfully' });
      } else {
        const subscription = await pushService.subscribeToNotifications();
        if (subscription) {
          setPushEnabled(true);
          const status = await pushService.getSubscriptionStatus();
          setSubscriptionCount(status.subscriptionCount);
          setMessage({ type: 'success', text: 'Push notifications enabled successfully! You will now receive notifications.' });
        } else {
          setMessage({ type: 'error', text: 'Failed to enable push notifications. Please check browser permissions.' });
        }
      }
    } catch (error) {
      console.error('Failed to toggle push notifications:', error);
      setMessage({ type: 'error', text: 'Failed to update push notification settings. Please try again.' });
    } finally {
      setPushLoading(false);
    }
  };

  if (!user) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Badge sx={{ fontSize: 32, color: mode === 'dark' ? consoleColors.sand : 'primary.main' }} />
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: mode === 'dark' ? '#ffffff' : '#1a1a1a'
          }}
        >
          Personnel Record
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* User Details */}
        <Grid item xs={12} md={6}>
          <Panel title="IDENTITY" icon={<Fingerprint fontSize="small" />} mode={mode}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-picture-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="profile-picture-upload">
                <Box sx={{ position: 'relative', mr: 3, cursor: 'pointer' }}>
                  <Box sx={{
                    p: 0.5,
                    border: `1px dashed ${mode === 'dark' ? consoleColors.sand : 'primary.main'}`,
                    borderRadius: '50%'
                  }}>
                    <UserAvatar
                      user={user}
                      sx={{ width: 80, height: 80 }}
                    />
                  </Box>
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: mode === 'dark' ? consoleColors.sand : 'primary.main',
                    color: mode === 'dark' ? consoleColors.abyssal : '#fff',
                    borderRadius: '50%',
                    p: 0.5,
                    boxShadow: 1,
                    display: 'flex'
                  }}>
                    <PhotoCamera fontSize="small" />
                  </Box>
                </Box>
              </label>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                  {user.name}
                </Typography>
                <Typography variant="body2" sx={{
                  color: mode === 'dark' ? consoleColors.sand : 'text.secondary',
                  fontFamily: 'monospace',
                  opacity: 0.8
                }}>
                  ID: {user.id}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : 'divider' }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  EMAIL ADDRESS
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {user.email || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  ASSIGNED ROLES
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {user.roles.map(role => (
                    <Box key={role} sx={{
                      border: `1px solid ${mode === 'dark' ? consoleColors.sand : '#ccc'}`,
                      px: 1,
                      py: 0.25,
                      fontSize: '0.75rem',
                      color: mode === 'dark' ? consoleColors.sand : 'text.primary',
                      fontFamily: 'monospace'
                    }}>
                      {role.toUpperCase()}
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Panel>
        </Grid>

        {/* Push Notifications */}
        <Grid item xs={12} md={6}>
          <Panel title="COMMUNICATION UPLINK" icon={<NotificationsActive fontSize="small" />} mode={mode}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enable real-time data push to this terminal.
            </Typography>

            {!pushService.isSupported() ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Push notifications are not supported.
              </Alert>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={pushEnabled}
                      onChange={handlePushToggle}
                      disabled={pushLoading}
                      color="primary"
                    />
                  }
                  label={pushEnabled ? "UPLINK ACTIVE" : "UPLINK OFFLINE"}
                  sx={{
                    '& .MuiTypography-root': {
                      fontFamily: 'monospace',
                      color: pushEnabled ? (mode === 'dark' ? consoleColors.sand : 'success.main') : 'text.secondary'
                    }
                  }}
                />

                {pushEnabled && subscriptionCount > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, ml: 7, fontFamily: 'monospace', color: mode === 'dark' ? consoleColors.sand : 'text.secondary' }}>
                    :: Active on {subscriptionCount} device{subscriptionCount > 1 ? 's' : ''}
                  </Typography>
                )}
              </>
            )}
          </Panel>
        </Grid>

        {/* Theme Settings */}
        <Grid item xs={12} md={6}>
          <Panel title="INTERFACE CONFIGURATION" icon={<SettingsSuggest fontSize="small" />} mode={mode}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Adjust visual output parameters.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={mode === 'dark'}
                  onChange={toggleTheme}
                  color="default"
                />
              }
              label={mode === 'dark' ? "DEEP OCEAN MODE" : "STANDARD MODE"}
              sx={{
                '& .MuiTypography-root': {
                  fontFamily: 'monospace'
                }
              }}
            />
          </Panel>
        </Grid>

        {/* Change Password */}
        <Grid item xs={12} md={6}>
          <Panel title="SECURITY PROTOCOLS" icon={<Security fontSize="small" />} mode={mode}>
            {message && (
              <Alert severity={message.type} sx={{
                mb: 3,
                borderRadius: 0,
                backgroundColor: message.type === 'error' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(56, 142, 60, 0.1)'
              }}>
                {message.text}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="CURRENT PASSWORD"
                name="oldPassword"
                type="password"
                value={formData.oldPassword}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                required
                variant="filled"
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />

              <TextField
                fullWidth
                label="NEW PASSWORD"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                required
                variant="filled"
                sx={{ mb: 2 }}
                helperText="Minimum 6 characters"
                InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />

              <TextField
                fullWidth
                label="CONFIRM PASSWORD"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                required
                variant="filled"
                sx={{ mb: 3 }}
                InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />

              <Button
                type="submit"
                variant="outlined"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderColor: mode === 'dark' ? consoleColors.sand : 'primary.main',
                  color: mode === 'dark' ? consoleColors.sand : 'primary.main',
                  borderRadius: 0,
                  fontFamily: 'monospace',
                  '&:hover': {
                    backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : 'rgba(15, 33, 52, 0.1)',
                    borderColor: mode === 'dark' ? consoleColors.sand : 'primary.main',
                  }
                }}
              >
                {loading ? 'PROCESSING...' : 'UPDATE CREDENTIALS'}
              </Button>
            </Box>
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserProfilePage;